import { useCallback, useEffect, useState } from "react";import { useSearchParams } from "react-router-dom";
import { api, formatApiError, fmtMoney } from "@/lib/api";
import { log } from "@/lib/log";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Clock, Landmark, FileText, Zap, Heart, Building2, ArrowRight } from "lucide-react";

const STRIPE_POLL_ATTEMPTS = 8;
const STRIPE_POLL_INTERVAL_MS = 1500;

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [tier, setTier] = useState(null);
  const [busy, setBusy] = useState(null); // which lookup_key is loading
  const [params, setParams] = useSearchParams();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [donation, setDonation] = useState(10);
  const [connect, setConnect] = useState(null);

  const load = useCallback(async () => {
    const [{ data: st }, { data: c }, { data: t }] = await Promise.all([
      api.get("/billing/status"),
      api.get("/billing/connect/status"),
      api.get("/billing/tier"),
    ]);
    setStatus(st);
    setConnect(c);
    setTier(t);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Handle Stripe Connect redirect back (`?connect=return` / `?connect=refresh`)
  useEffect(() => {
    const cparam = params.get("connect");
    if (!cparam) return;
    if (cparam === "return") toast.success("Business bank details saved with Stripe");
    if (cparam === "refresh") toast("Onboarding link expired — starting fresh");
    params.delete("connect");
    setParams(params, { replace: true });
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const startConnectOnboarding = async () => {
    setBusy("connect_onboard");
    try {
      const { data } = await api.post("/billing/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.onboarding_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(null); }
  };

  // Handle Stripe redirect back with session_id — mount-only, subsequent poll
  // iterations reuse the same `load` reference via closure.
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    const auto = params.get("auto_checkout");
    if (cancelled) toast.error("Payment cancelled");
    if (auto && ["checkmate_community", "checkmate_pro", "checkmate_pro_plus"].includes(auto)) {
      params.delete("auto_checkout");
      setParams(params, { replace: true });
      // Fire the tier checkout on next tick so `checkout` sees latest state.
      setTimeout(() => checkout(auto), 400);
    }
    if (!sid) return;
    (async () => {
      for (let i = 0; i < STRIPE_POLL_ATTEMPTS; i++) {
        try {
          const { data } = await api.get(`/billing/payments/status/${sid}`);
          if (data.payment_status === "paid") { toast.success("Payment received. Thank you!"); load(); break; }
        } catch (err) {
          log.warn("Poll billing status failed:", err?.message || err);
        }
        await new Promise((r) => setTimeout(r, STRIPE_POLL_INTERVAL_MS));
      }
      params.delete("session_id");
      setParams(params, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async (lookup_key, quantity = 1, endpoint = "/billing/checkout", extra = {}) => {
    setBusy(lookup_key);
    try {
      const origin_url = window.location.origin;
      const body = endpoint === "/billing/checkout" ? { lookup_key, quantity, origin_url } : { ...extra, origin_url };
      const { data } = await api.post(endpoint, body);
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(null); }
  };

  if (!status) return <div className="p-10 font-mono">Loading…</div>;

  const pro = status.pro;
  const p = status.pricing;

  return (
    <div className="p-10">
      <div className="overline mb-2">§ BILLING</div>
      <h1 className="font-heading font-black text-5xl tracking-tight mb-2">Plan &amp; Billing</h1>
      <p className="text-sm text-muted-foreground mb-8">Checkmate Payroll is pay-as-you-go. Direct-deposit companies get Pro for free.</p>

      {/* Checkmate Revenue Hub — 3-tier subscription cards */}
      <CheckmateTiers status={status} tier={tier} busy={busy} checkout={checkout} refresh={load} />

      {/* Direct Deposit Setup — Stripe Connect Express */}
      <div className="hard-card p-6 mb-8" data-testid="connect-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <div className="overline mb-2 flex items-center gap-2"><Building2 className="w-4 h-4" /> DIRECT DEPOSIT SETUP</div>
            <div className="font-heading font-black text-2xl tracking-tight mb-1">Link your business bank account</div>
            <p className="font-mono text-xs text-muted-foreground leading-relaxed max-w-xl">
              Checkmate Payroll uses Stripe Connect to pull funds from your business bank and push ACH deposits to employees on payroll runs. Onboarding takes ~2 minutes.
            </p>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="connect-flags">
              <ConnectPill label="Account linked" active={!!connect?.account_id} />
              <ConnectPill label="Details submitted" active={!!connect?.details_submitted} />
              <ConnectPill label="Payouts enabled" active={!!connect?.payouts_enabled} />
              <ConnectPill label="Charges enabled" active={!!connect?.charges_enabled} />
            </div>
            {connect?.requirements_disabled_reason && (
              <div className="mt-3 font-mono text-[11px] text-bad" data-testid="connect-blocker">
                Stripe needs more info: {connect.requirements_disabled_reason.replace(/[_.]/g, " ")}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <button
              type="button"
              data-testid="connect-onboard-btn"
              onClick={startConnectOnboarding}
              disabled={busy === "connect_onboard"}
              className="hard-btn px-5 py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              {busy === "connect_onboard" ? "Redirecting…" : (connect?.payouts_enabled ? "Update bank details" : (connect?.account_id ? "Finish onboarding →" : "Link business bank"))}
            </button>
            {connect?.verified_at && (
              <div className="font-mono text-[10px] text-muted-foreground">
                Verified {new Date(connect.verified_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Usage-based fees */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="hard-card p-6" data-testid="fee-ach-card">
          <div className="overline mb-2 flex items-center gap-2"><Landmark className="w-4 h-4" /> DIRECT-DEPOSIT CONVENIENCE FEE</div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-heading font-black text-4xl tracking-tight">$0.50</span>
            <span className="text-sm text-muted-foreground">/ direct-deposit stub</span>
          </div>
          <p className="font-mono text-xs text-muted-foreground mb-5 leading-relaxed">Charged when you submit a payroll run that includes direct-deposit payments. Covers the ACH processing cost.</p>
          <div className="border-2 border-ink p-3 mb-4 font-mono text-xs">
            <div className="flex justify-between mb-1"><span>DD stubs · last 30 days</span><b>{status.dd_stubs_last_30d}</b></div>
            <div className="flex justify-between font-black"><span>Approx. next invoice</span><b>{fmtMoney((status.dd_stubs_last_30d || 0) * 0.50)}</b></div>
          </div>
          <p className="text-[11px] text-muted-foreground">Fees are itemized in your recent transactions below.</p>
        </div>

        <div className="hard-card p-6" data-testid="fee-w2-card">
          <div className="overline mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> W-2 BULK GENERATION</div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-heading font-black text-4xl tracking-tight">$2.00</span>
            <span className="text-sm text-muted-foreground">/ W-2</span>
          </div>
          <p className="font-mono text-xs text-muted-foreground mb-5 leading-relaxed">One-time charge to generate and file W-2s for all employees for a tax year.</p>
          <div className="flex items-center gap-2 mb-3">
            <label className="overline">Year</label>
            <input data-testid="w2-year" value={year} onChange={(e) => setYear(e.target.value)} className="border-2 border-ink px-2 py-1 font-mono text-sm w-24" />
          </div>
          <button data-testid="charge-w2-btn" onClick={() => checkout("w2_bulk_per_employee", 1, "/billing/charge/w2-bulk", { year })} disabled={busy === "w2_bulk_per_employee"} className="hard-btn w-full py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2">
            <Zap className="w-4 h-4" /> {busy === "w2_bulk_per_employee" ? "Redirecting…" : `Generate W-2 Batch for ${year}`}
          </button>
        </div>
      </div>

      {/* Donation card — "Support the Project" (Community tier "Start with $1" CTA scrolls here) */}
      <div id="donation-section" className="hard-card p-6 mb-8 scroll-mt-16" data-testid="donation-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <div className="overline mb-2 flex items-center gap-2"><Heart className="w-4 h-4 text-warn" /> SUPPORT THE PROJECT</div>
            <div className="font-heading font-black text-2xl tracking-tight mb-2">Love Checkmate Payroll? Chip in.</div>
            <p className="font-mono text-xs text-muted-foreground leading-relaxed max-w-xl">
              Every donation keeps the free tier free for small teams and funds new features (mobile app, expanded state tax coverage, richer reports). One-time — no recurring charge.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[5, 10, 25, 50].map((amt) => (
              <button
                key={amt}
                type="button"
                data-testid={`donate-preset-${amt}`}
                onClick={() => setDonation(amt)}
                className={`border-2 border-ink px-3 py-2 font-mono text-xs uppercase tracking-widest ${donation === amt ? "bg-ink text-white" : "bg-white hover:bg-ink hover:text-white"}`}
              >${amt}</button>
            ))}
            <div className="flex items-center border-2 border-ink">
              <span className="px-2 font-mono text-sm">$</span>
              <input
                type="number" min="1" max="500" step="1"
                value={donation}
                onChange={(e) => setDonation(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                data-testid="donate-custom-amount"
                className="w-20 px-2 py-1.5 font-mono text-sm outline-none"
              />
            </div>
            <button
              type="button"
              data-testid="donate-btn"
              disabled={busy === "donation"}
              onClick={() => checkout("donation", 1, "/billing/charge/donation", { amount_usd: donation })}
              className="hard-btn px-5 py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2"
            >
              <Heart className="w-4 h-4" /> {busy === "donation" ? "Redirecting…" : `Donate ${fmtMoney(donation)}`}
            </button>
          </div>
        </div>
      </div>

      {/* Recent transactions */}
      <div className="hard-card overflow-x-auto">
        <div className="overline p-4 border-b-2 border-ink flex items-center gap-2"><Clock className="w-4 h-4" /> RECENT TRANSACTIONS</div>
        <table className="w-full font-mono text-sm" data-testid="txns-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">When</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Description</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Qty</th>
              <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Amount</th>
              <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Status</th>
            </tr>
          </thead>
          <tbody>
            {(status.recent_transactions || []).length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">No transactions yet.</td></tr>}
            {(status.recent_transactions || []).map((t, i) => (
              <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"}>
                <td className="px-4 py-3 border-t border-ink/20">{new Date(t.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 border-t border-ink/20">{t.lookup_key}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right">{t.quantity}</td>
                <td className="px-4 py-3 border-t border-ink/20 text-right font-black">{fmtMoney((t.amount_cents || 0) / 100)}</td>
                <td className="px-4 py-3 border-t border-ink/20">
                  <PaymentStatusPill status={t.payment_status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConnectPill({ label, active }) {
  return (
    <span className={`inline-flex items-center gap-1 border-2 border-ink px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${active ? "bg-good text-white" : "bg-white text-ink"}`}>
      {active ? <CheckCircle2 className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />} {label}
    </span>
  );
}

function PaymentStatusPill({ status }) {
  let cls;
  if (status === "paid") cls = "bg-good text-white";
  else if (status === "pending") cls = "bg-warn text-ink";
  else cls = "bg-muted text-ink";
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${cls}`}>{status}</span>;
}

const TIER_CARDS = [
  {
    key: "checkmate_community",
    name: "Community",
    price: "$1",
    unit: "/ mo",
    tagline: "Solo operators + non-profits · basic timekeeping",
    features: [
      "Time tracking + manual paychecks",
      "Employee self-service portal",
      "W-2 generation",
    ],
    accent: "bg-white text-ink border-2 border-ink",
    cta: "Start with $1",
  },
  {
    key: "checkmate_pro",
    name: "Pro",
    price: "$2,500",
    unit: "/ mo",
    tagline: "Full compliance engine · 5-25 employee shops",
    features: [
      "Federal + 50-state tax compliance",
      "Predictive Payroll + Reports",
      "HR E-Sign + Mapbox geofencing",
      "Twilio SMS + Stripe Connect ACH",
    ],
    accent: "bg-warn/10 text-ink border-2 border-warn",
    cta: "Start Pro",
    highlight: true,
  },
  {
    key: "checkmate_pro_plus",
    name: "Pro Plus",
    price: "$4,500",
    unit: "/ mo",
    tagline: "Agency automation · 25+ employees",
    features: [
      "Everything in Pro",
      "AI Office Manager Concierge",
      "Client Billing packets (PDF)",
      "Remote Work Monitoring",
      "White-glove onboarding",
    ],
    accent: "bg-ink text-white border-2 border-warn shadow-hard-lg",
    cta: "Request Pro Plus",
    roi: "Labor savings ~$4,100/mo · Net cost ~$400/mo",
  },
];

function CheckmateTiers({ status, tier, busy, checkout, refresh }) {
  const [syncing, setSyncing] = useState(false);
  // Server-truth: tier = community / pro / pro_plus / free
  const serverTier = tier?.tier;
  const loyaltyPrice = tier?.loyalty_price_usd;        // e.g. 1500 for Logan
  const loyaltyFullSuite = tier?.loyalty_full_suite;   // Logan gets Pro Plus features at the reduced rate
  const currentTier = serverTier === "pro_plus" ? "checkmate_pro_plus"
    : serverTier === "pro" ? "checkmate_pro"
    : serverTier === "community" ? "checkmate_community"
    : null;

  const scrollToDonation = () => {
    const el = document.getElementById("donation-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const syncLoyalty = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/billing/sync-loyalty-subscription", {});
      if (data.already_synced) {
        toast.success("Stripe subscription is already at your loyalty rate — nothing to update.");
      } else {
        toast.success(`Stripe subscription synced to your loyalty rate ($${(data.unit_amount_cents / 100).toLocaleString("en-US")}/mo).`);
        if (refresh) refresh();
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setSyncing(false);
  };

  const fmtPrice = (usd) => `$${Number(usd).toLocaleString("en-US")}`;

  return (
    <div className="mb-8" data-testid="checkmate-tiers">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><Sparkles className="w-4 h-4" /> § CHECKMATE REVENUE HUB</div>
          <h2 className="font-heading font-black text-3xl tracking-tight">Pick your tier</h2>
        </div>
        {loyaltyPrice ? (
          <div className="font-mono text-[11px] text-good font-black" data-testid="loyalty-badge">
            Loyalty pricing active: {fmtPrice(loyaltyPrice)}/mo
          </div>
        ) : (
          <div className="font-mono text-[11px] text-muted-foreground">Every plan billed monthly · cancel anytime.</div>
        )}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {TIER_CARDS.map((t) => {
          const isCurrent = currentTier === t.key;
          const isLoyaltyPro = loyaltyPrice != null && t.key === "checkmate_pro";
          // Loyalty client with full-suite: show Pro Plus card as "included"
          const includedViaLoyalty = loyaltyFullSuite && t.key === "checkmate_pro_plus";
          const displayPrice = isLoyaltyPro ? fmtPrice(loyaltyPrice) : t.price;
          return (
            <div key={t.key} className={`p-6 flex flex-col ${t.accent}`} data-testid={`tier-card-${t.key}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="overline">§ {t.name.toUpperCase()}</div>
                {isLoyaltyPro && <span className="px-2 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest font-black" data-testid="tier-loyalty-pill">Loyalty Rate</span>}
                {includedViaLoyalty && <span className="px-2 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest font-black" data-testid="tier-included-pill">Included</span>}
                {isCurrent && !isLoyaltyPro && !includedViaLoyalty && <span className="px-2 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest font-black" data-testid={`tier-current-${t.key}`}>Current</span>}
                {!isCurrent && !isLoyaltyPro && !includedViaLoyalty && t.highlight && <span className="px-2 py-0.5 bg-warn text-ink text-[10px] uppercase tracking-widest font-black">Most Popular</span>}
              </div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-heading font-black text-4xl tracking-tight" data-testid={`tier-price-${t.key}`}>{displayPrice}</span>
                <span className="font-mono text-xs opacity-70">{t.unit}</span>
              </div>
              <div className="font-mono text-[11px] opacity-70 mb-4">
                {isLoyaltyPro ? "Locked loyalty rate · full-suite access" : t.tagline}
              </div>
              {t.roi && !isLoyaltyPro && !includedViaLoyalty && (
                <div className="border-2 border-good bg-good/20 text-white p-2 mb-4 font-mono text-[10px] leading-relaxed">
                  <div className="font-black">{t.roi}</div>
                </div>
              )}
              <ul className="font-mono text-xs space-y-1.5 mb-6 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              {isLoyaltyPro ? (
                <div className="space-y-2" data-testid="tier-loyalty-block">
                  <div className="px-4 py-2.5 font-mono uppercase text-xs tracking-widest font-black text-center bg-good text-white" data-testid="tier-loyalty-msg">
                    Loyalty Plan: {fmtPrice(loyaltyPrice)}/mo · Locked
                  </div>
                  <button
                    type="button"
                    onClick={syncLoyalty}
                    disabled={syncing}
                    data-testid="sync-loyalty-btn"
                    className="w-full px-4 py-2 font-mono uppercase text-[10px] tracking-widest font-black border-2 border-ink bg-white text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-50"
                    title="Rewrite your active Stripe subscription to the loyalty rate"
                  >
                    {syncing ? "Syncing Stripe…" : "Sync Stripe Subscription →"}
                  </button>
                </div>
              ) : includedViaLoyalty ? (
                <div className="px-4 py-2.5 font-mono uppercase text-[10px] tracking-widest font-black text-center bg-good text-white" data-testid="tier-included-msg">
                  Included in your Loyalty Plan
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={`tier-cta-${t.key}`}
                  disabled={busy === t.key || isCurrent}
                  onClick={() => t.key === "checkmate_community" ? scrollToDonation() : checkout(t.key)}
                  className={`px-4 py-2.5 font-mono uppercase text-xs tracking-widest font-black inline-flex items-center justify-center gap-2 ${isCurrent ? "bg-good text-white cursor-not-allowed" : (t.key === "checkmate_pro_plus" ? "bg-warn text-ink hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform" : "bg-ink text-white hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform")}`}
                >
                  {isCurrent ? "✓ Active" : (busy === t.key ? "Redirecting…" : `${t.cta} →`)}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
